import * as z from "zod/v4";

/**
 * Surfaces the state of the HubSpot mirror ingest pipeline and lifecycle
 * runtime. Powers the admin Sync tab: "last sync, row counts, schedule
 * existence, trigger a run". Read-only.
 */
export const getHubspotSyncHealthInputSchema = z.object({});
export type GetHubspotSyncHealthInput = z.infer<
  typeof getHubspotSyncHealthInputSchema
>;

const bookmarkSchema = z.object({
  kind: z.string(),
  cursorAt: z.date(),
  lastDealCursor: z.string().nullable(),
  updatedAt: z.date(),
});

const scheduleExistenceSchema = z.object({
  scheduleId: z.string(),
  exists: z.boolean(),
  /** Optional error message if the describe call failed for a non-not-found reason. */
  describeError: z.string().nullable(),
});

const rowCountsSchema = z.object({
  hubspotDeals: z.number().int().min(0),
  hubspotDealsLinked: z.number().int().min(0),
  hubspotPipelines: z.number().int().min(0),
  hubspotStages: z.number().int().min(0),
  hubspotOwners: z.number().int().min(0),
  lifecycleEvents: z.number().int().min(0),
  progressRows: z.number().int().min(0),
  progressWithFirstRevenue: z.number().int().min(0),
  progressRevenueGenerating: z.number().int().min(0),
  progressChurned: z.number().int().min(0),
});
export type HubspotSyncRowCounts = z.infer<typeof rowCountsSchema>;

const deltaSchema = z.object({
  kind: z.string(),
  /** Most recent event timestamp. Null if we have no events yet. */
  mostRecentEventAt: z.date().nullable(),
  /** Events inserted in the last 24h. */
  last24h: z.number().int().min(0),
  /** Events inserted in the last 7d. */
  last7d: z.number().int().min(0),
});
export type HubspotSyncDelta = z.infer<typeof deltaSchema>;

export const getHubspotSyncHealthOutputSchema = z.object({
  bookmarks: z.array(bookmarkSchema),
  schedules: z.array(scheduleExistenceSchema),
  rowCounts: rowCountsSchema,
  lifecycleEventActivity: z.array(deltaSchema),
  /** When the `revenue_checked_at` column was last touched (proxy for lifecycle recompute activity). */
  lifecycleRecomputeLastRunAt: z.date().nullable(),
});
export type GetHubspotSyncHealthOutput = z.infer<
  typeof getHubspotSyncHealthOutputSchema
>;

export const getHubspotSyncHealth = {
  name: "getHubspotSyncHealth" as const,
  input: getHubspotSyncHealthInputSchema,
  output: getHubspotSyncHealthOutputSchema,
};
